import { useEffect, useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  Line,
  ComposedChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { supabase, type Snapshot } from "./lib/supabase";

const RANGES = {
  "1D": 1,
  "1W": 7,
  "1M": 30,
  "3M": 90,
  "1Y": 365,
  "ALL": null as number | null,
} as const;

type RangeKey = keyof typeof RANGES;

function fmtUSD(cents: number): string {
  const dollars = cents / 100;
  return dollars.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: Math.abs(dollars) < 1000 ? 2 : 0,
  });
}

function fmtTime(ts: string, rangeDays: number | null): string {
  const d = new Date(ts);
  if (rangeDays !== null && rangeDays <= 1) {
    return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  }
  if (rangeDays !== null && rangeDays <= 30) {
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  }
  return d.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
}

export default function App() {
  const [snapshots, setSnapshots] = useState<Snapshot[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [range, setRange] = useState<RangeKey>("1M");
  const [lastFetched, setLastFetched] = useState<Date | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const days = RANGES[range];
      let q = supabase
        .from("snapshots")
        .select("id, ts, cash_cents, portfolio_value_cents, total_value_cents, net_deposits_cents")
        .order("ts", { ascending: true });
      if (days !== null) {
        const since = new Date(Date.now() - days * 86400_000).toISOString();
        q = q.gte("ts", since);
      }
      // For long ranges, cap result count — Supabase free tier caps queries at ~1000 rows by default.
      q = q.limit(5000);
      const { data, error } = await q;
      if (cancelled) return;
      if (error) { setError(error.message); return; }
      setError(null);
      setSnapshots(data ?? []);
      setLastFetched(new Date());
    }
    load();
    const id = setInterval(load, 60_000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
  }, [range]);

  const chartData = useMemo(() => {
    if (!snapshots) return [];
    return snapshots.map(s => ({
      ts: s.ts,
      total: s.total_value_cents / 100,
      deposits: s.net_deposits_cents / 100,
    }));
  }, [snapshots]);

  const latest = snapshots && snapshots.length > 0 ? snapshots[snapshots.length - 1] : null;
  const first  = snapshots && snapshots.length > 0 ? snapshots[0] : null;

  const pnlInRange = useMemo(() => {
    if (!latest || !first) return null;
    return (latest.total_value_cents - first.total_value_cents);
  }, [latest, first]);

  const lifetimePnl = useMemo(() => {
    if (!latest) return null;
    return latest.total_value_cents - latest.net_deposits_cents;
  }, [latest]);

  const rangeDays = RANGES[range];

  return (
    <div className="app">
      <div className="header">
        <h1>Portfolio</h1>
        <div className="value-now">
          {latest ? fmtUSD(latest.total_value_cents) : "—"}
        </div>
        <div className="value-delta">
          {pnlInRange !== null ? (
            <>
              <span className={pnlInRange >= 0 ? "up" : "down"}>
                {pnlInRange >= 0 ? "+" : ""}{fmtUSD(pnlInRange)}
              </span>
              {" "}
              <span>over {range}</span>
            </>
          ) : "loading…"}
        </div>
      </div>

      {error && <div className="error">{error}</div>}

      <div className="chart-card">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="totalFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"  stopColor="#5cd6a0" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#5cd6a0" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="ts"
              tickFormatter={(ts: string) => fmtTime(ts, rangeDays)}
              tick={{ fill: "#8a94a4", fontSize: 11 }}
              minTickGap={32}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tickFormatter={(v: number) =>
                v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${v.toFixed(0)}`
              }
              tick={{ fill: "#8a94a4", fontSize: 11 }}
              width={48}
              axisLine={false}
              tickLine={false}
              domain={["dataMin - 5", "dataMax + 5"]}
            />
            <Tooltip
              contentStyle={{
                background: "#14181d",
                border: "1px solid #2b3038",
                borderRadius: 8,
                fontSize: 12,
              }}
              labelFormatter={(ts: string) => new Date(ts).toLocaleString()}
              formatter={(v: number, name: string) => [
                fmtUSD(Math.round(v * 100)),
                name === "total" ? "Total value" : "Net deposits",
              ]}
            />
            <Area
              type="monotone"
              dataKey="total"
              stroke="#5cd6a0"
              strokeWidth={2}
              fill="url(#totalFill)"
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="deposits"
              stroke="#6c8cff"
              strokeWidth={1.5}
              strokeDasharray="4 3"
              dot={false}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="range-row">
        {(Object.keys(RANGES) as RangeKey[]).map(k => (
          <button
            key={k}
            className={"range-btn" + (k === range ? " active" : "")}
            onClick={() => setRange(k)}
          >{k}</button>
        ))}
      </div>

      <div className="stats">
        <div className="stat">
          <div className="label">Lifetime P&L</div>
          <div className="num">
            {lifetimePnl !== null ? (
              <span style={{ color: lifetimePnl >= 0 ? "var(--accent)" : "var(--negative)" }}>
                {lifetimePnl >= 0 ? "+" : ""}{fmtUSD(lifetimePnl)}
              </span>
            ) : "—"}
          </div>
        </div>
        <div className="stat">
          <div className="label">Net Deposits</div>
          <div className="num">{latest ? fmtUSD(latest.net_deposits_cents) : "—"}</div>
        </div>
        <div className="stat">
          <div className="label">Cash</div>
          <div className="num">{latest ? fmtUSD(latest.cash_cents) : "—"}</div>
        </div>
        <div className="stat">
          <div className="label">In Positions</div>
          <div className="num">{latest ? fmtUSD(latest.portfolio_value_cents) : "—"}</div>
        </div>
      </div>

      <div className="status">
        {lastFetched ? `Updated ${lastFetched.toLocaleTimeString()}` : "—"}
      </div>
    </div>
  );
}
